import Button from 'components/Button';
import Field from 'components/Form/Field';
import {TextAreaProps as BaseTextAreaProps} from 'components/Form/TextArea';
import {FormContextString} from 'components/Form/types';
import {getRegisterRules} from 'components/Form/utils';
import Overlay from 'components/Layout/Overlay';
import TooltipWrapper from 'components/Tooltip';
import {useTooltip} from 'components/Tooltip/hook';
import useFormError from 'hooks/useFormErrors';
import useOutsideDetector from 'hooks/useOutsideDetector';
import {FC, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';
import {Option} from 'types';

import {insertVariableIntoTextarea} from '../InputTextInsertVariables/utils';
import {useAutosize} from '../TextArea/utils';
import InsertVariablesContainer from './container';
import {wrapperClassName} from './styles.module.css';

type TextAreaProps = {
    buttonTooltip?: string;
    options: Option[];
} & BaseTextAreaProps;

const TextAreaInsertVariables: FC<TextAreaProps> = ({
    buttonTooltip = null,
    className,
    classNameTextArea,
    disabled,
    error,
    helpText,
    id,
    label,
    maxLength,
    name,
    options,
    required,
    requiredType,
    resize = 'auto',
    rows = 2,
    rules,
    ...rest
}) => {
    const {setTooltipRef} = useTooltip();

    const {register} = useFormContext<FormContextString>();

    const registerRules = getRegisterRules({disabled, required, rules});

    const {ref, ...registration} = register(name, registerRules);

    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    useAutosize({
        disabled: resize !== 'auto',
        name,
        ref: internalRef,
    });

    const fieldError = useFormError(name, error);
    const showErrors = !!fieldError;

    const inputRef = useRef<HTMLButtonElement | null>(null);
    const optionsRef = useRef<HTMLDivElement | null>(null);
    const [showOptions, setShowOptions] = useState(false);
    useOutsideDetector([inputRef, optionsRef], () => setShowOptions(false));

    const handleOptionSelect = (option: Option) => {
        const textareaElement = internalRef.current;
        if (!textareaElement) throw new Error('Textarea not found');
        insertVariableIntoTextarea(textareaElement, option.value);
        setShowOptions(false);
    };

    const button = (
        <div className="absolute right-px top-px p-2">
            <Button
                className="border-solid shadow"
                disabled={disabled}
                icon="plus"
                kind="secondary"
                onClick={
                    !disabled ? () => setShowOptions(!showOptions) : undefined
                }
                ref={inputRef}
                size="xs"
            />
        </div>
    );
    const optionsButton =
        buttonTooltip !== undefined ? (
            <TooltipWrapper message={buttonTooltip}>{button}</TooltipWrapper>
        ) : (
            button
        );

    return (
        <>
            <Field
                className={className}
                disabled={registerRules?.disabled}
                error={fieldError}
                helpText={helpText}
                label={label}
                maxLength={maxLength}
                name={name}
                required={!!registerRules?.required}
                requiredType={requiredType}
                type="textarea"
            >
                <div
                    aria-expanded={showOptions}
                    className={twMerge(
                        'relative',
                        'input-wrapper flex items-start',
                        showErrors && 'input-invalid',
                        wrapperClassName,
                    )}
                    data-disabled={disabled}
                    data-invalid={!!error}
                    data-type="input"
                    tabIndex={-1}
                >
                    <textarea
                        className={twMerge(
                            'h-fit resize-none',
                            fieldError && 'input-invalid',
                            'border-none focus:ring-0',
                            classNameTextArea,
                        )}
                        id={id || name}
                        maxLength={maxLength}
                        {...registration}
                        ref={(element) => {
                            ref(element);
                            internalRef.current = element;
                            setTooltipRef(element);
                        }}
                        rows={rows}
                        {...rest}
                    />
                    {optionsButton}
                </div>
            </Field>
            {showOptions && (
                <Overlay
                    element={inputRef.current}
                    layoutLayer="z-tooltip"
                    layoutMethod="right-corner"
                    layoutOffset={6}
                >
                    <InsertVariablesContainer
                        className="min-w-80"
                        onSelect={(option) => handleOptionSelect(option)}
                        options={options}
                        ref={optionsRef}
                    />
                </Overlay>
            )}
        </>
    );
};

export default TextAreaInsertVariables;
